import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { api } from '../../lib/api';
import type { NotificationItem, NotificationSeverity } from '../../lib/api';

const SEVERITY_ICON: Record<NotificationSeverity, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  action_required: AlertCircle,
};

const SEVERITY_COLOR: Record<NotificationSeverity, string> = {
  info: 'text-ui-accent',
  warning: 'text-ui-warning',
  action_required: 'text-ui-danger',
};

function formatWhen(epochSeconds: number | null): string {
  if (!epochSeconds) return '';
  return new Date(epochSeconds * 1000).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

const POLL_INTERVAL_MS = 60_000;
const DROPDOWN_LIMIT = 8;

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const loadCount = useCallback(() => {
    api.notifications.list().then((r) => setUnreadCount(r.unreadCount)).catch(() => { /* ignore */ });
  }, []);

  useEffect(() => {
    loadCount();
    const interval = setInterval(loadCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadCount]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  function toggleOpen() {
    setOpen((o) => {
      const next = !o;
      if (next) {
        api.notifications.list().then((r) => {
          setItems(r.items.slice(0, DROPDOWN_LIMIT));
          setUnreadCount(r.unreadCount);
          setLoaded(true);
        }).catch(() => { /* ignore */ });
      }
      return next;
    });
  }

  async function handleItemClick(n: NotificationItem) {
    setOpen(false);
    if (!n.readAt) {
      await api.notifications.markRead(n.id).catch(() => { /* ignore */ });
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    if (n.actionUrl) navigate(n.actionUrl);
    else navigate('/notifications');
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggleOpen}
        title="Notificaciones"
        className="relative w-8 h-8 flex items-center justify-center rounded-md text-ui-text-subtle hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
        aria-label="Notificaciones"
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ui-danger opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-ui-danger" />
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.13 }}
            className="absolute right-0 top-10 w-80 max-w-[90vw] bg-ui-surface border border-ui-border rounded-xl shadow-xl z-dropdown overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-ui-border flex items-center justify-between">
              <p className="text-sm font-medium text-ui-text">Notificaciones</p>
              {unreadCount > 0 && (
                <span className="text-xs text-ui-text-subtle">{unreadCount} sin leer</span>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {!loaded && (
                <p className="px-4 py-6 text-sm text-ui-text-subtle text-center">Cargando…</p>
              )}
              {loaded && items.length === 0 && (
                <p className="px-4 py-6 text-sm text-ui-text-subtle text-center">Sin notificaciones por ahora.</p>
              )}
              {items.map((n) => {
                const Icon = SEVERITY_ICON[n.severity];
                return (
                  <button
                    key={n.id}
                    onClick={() => handleItemClick(n)}
                    className="w-full text-left px-4 py-3 border-b border-ui-border last:border-b-0 hover:bg-ui-text/[0.04] transition-colors flex gap-3"
                  >
                    <Icon size={16} className={`shrink-0 mt-0.5 ${SEVERITY_COLOR[n.severity]}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-ui-text font-medium truncate">{n.title}</p>
                        {!n.readAt && <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-ui-danger" />}
                      </div>
                      {n.body && <p className="text-sm text-ui-text-muted line-clamp-2 mt-0.5">{n.body}</p>}
                      <p className="text-xs text-ui-text-subtle mt-1">{formatWhen(n.createdAt)}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="w-full px-4 py-2.5 text-sm text-ui-accent hover:bg-ui-text/[0.06] transition-colors text-center border-t border-ui-border"
            >
              Ver todas
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
